
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
AuthProviderWithHistory
********************************************************************************************
Base on: https://auth0.com/blog/complete-guide-to-react-user-authentication/

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       23rd December 2020

*******************************************************************************************/

import React                from "react";
import { useHistory }       from "react-router-dom";
import { 
  Auth0Provider, 
  useAuth0
}                           from "@auth0/auth0-react";
import { App as CapApp }    from '@capacitor/app';
import { Browser }          from '@capacitor/browser';
import config               from "../config";
import { useDevice }        from "contexts";

// AUTH0
const {
  rootUrl,
  auth0 : {
    domain,
    clientId,
    scope,
    audience,
    useRefreshTokens,
    useRefreshTokensFallback,
    cacheLocation,
    nativeCallbackUri,
  }
}                         = config;

const NativeAuth0HandleRedirect = () => {

  const { isNative }                = useDevice();
  const { client }                  = useAuth0();
  
  React.useEffect(() => {

    if (!isNative) 
      return;

    const listener = async ({url}) => {
      try {
        if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
          await client.handleRedirectCallback(url);
        }
      } catch (error) {
        console.error("Error Handling Redirect:");
        console.error(error);
      } finally {
        try {
          await Browser.close();
        } catch (closeError) {
          console.warn("Failed to Close Browser:");
          console.warn(closeError);
        }
      }
    };

    let remove;
    (async () => {
      remove = (await CapApp.addListener('appUrlOpen', listener)).remove;
    })();

    return () => {
      remove?.();
    };

  }, [client, isNative]);

  return null;
};

export const Auth0ProviderWithHistory = ({ children }) => {

  const { isNative }  = useDevice();
  const history       = useHistory();

  // https://community.auth0.com/t/redirect-after-login-page/63011/3
  const onRedirectCallback = React.useCallback( (appState) => {
      const { returnTo = window.location.pathname } = appState || {};
      history.push(returnTo);
  }, [history]);

  return (
    <Auth0Provider
      domain                    = { domain }
      clientId                  = { clientId }
      authorizationParams       = {{
        redirect_uri  : isNative ? nativeCallbackUri : rootUrl,
        scope         : scope,
        audience      : audience
      }}
      useRefreshTokens          = { useRefreshTokens }
      useRefreshTokensFallback  = { useRefreshTokensFallback }
      cacheLocation             = { cacheLocation }
      onRedirectCallback        = { onRedirectCallback}
    >
      {
        false && 
        <NativeAuth0HandleRedirect />
      }
      <React.Fragment>
        {children}
      </React.Fragment>
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;